import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from './useMultiTenant';
import { toast } from '@/hooks/use-toast';
import type { ProductType, ProductTypeFormData } from '@/types/productType';

/**
 * S3 (I.178, migration 20261128130000): recusas do banco ao gravar tipo. TIPO-CODIGO-UX (I.178): o código interno não
 * aparece mais na tela, então as mensagens não falam dele.
 */
export function productTypeErrorMessage(error: unknown): { title: string; description: string } | null {
  const e = error as { code?: string; message?: string; details?: string } | null;
  const text = `${e?.message ?? ''} ${e?.details ?? ''}`;
  if (text.includes('Código em uso por')) {
    return { title: 'Tipo em uso', description: 'Este tipo já tem produtos e não pode ser alterado desse jeito' };
  }
  if (e?.code === '23505' && text.includes('uq_product_types_company_code')) {
    return { title: 'Tente de novo', description: 'Outro tipo parecido foi criado ao mesmo tempo; salve de novo' };
  }
  if (e?.code === '23505') {
    return { title: 'Nome duplicado', description: 'Já existe um tipo de produto com este nome' };
  }
  return null;
}

/**
 * TIPO-CODIGO-UX (Diretor 31/10, I.178): o código interno do tipo é campo funcional do sistema — gerado a partir do
 * nome, sem interferência do usuário. Minúsculas, sem acento, espaço vira "_", só letras, números e "_".
 */
export const internalCodeFromName = (name: string) =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

/**
 * Códigos fixos dos tipos de sistema (produto padrão, cilindro, gás/carga, equipamento) e o "MP" que a entrada de
 * compra cria: um tipo do usuário nunca recebe um deles (nem ganha o comportamento especial que o código liga).
 */
const RESERVED_TYPE_CODES = new Set(['product', 'cylinder', 'gas', 'equipment', 'mp']);

export const useProductTypes = () => {
  const { companyId } = useMultiTenant();
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchProductTypes = useCallback(async () => {
    if (!companyId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('product_types')
        .select('*')
        .eq('company_id', companyId)
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setProductTypes((data as unknown as ProductType[]) || []);
    } catch (error) {
      console.error('Erro ao buscar tipos de produto:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os tipos de produto',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchProductTypes();
  }, [fetchProductTypes]);

  /**
   * TIPO-CODIGO-UX (I.178): código gerado a partir do nome, único na empresa contra todos os tipos (inclusive inativos,
   * sem diferença de maiúscula, como a regra do banco) e fora dos códigos reservados; ocupado → acrescenta _2, _3...
   */
  const generateInternalCode = async (name: string): Promise<string> => {
    const { data, error } = await supabase
      .from('product_types')
      .select('internal_code')
      .eq('company_id', companyId as string);
    if (error) throw error;
    const taken = new Set((data ?? []).map((t) => (t.internal_code ?? '').trim().toLowerCase()).filter(Boolean));
    const base = internalCodeFromName(name) || 'tipo';
    const free = (code: string) => !taken.has(code) && !RESERVED_TYPE_CODES.has(code);
    if (free(base)) return base;
    for (let i = 2; i < 1000; i++) {
      if (free(`${base}_${i}`)) return `${base}_${i}`;
    }
    throw new Error('Não foi possível gerar o código do tipo');
  };

  const createProductType = async (data: ProductTypeFormData): Promise<ProductType | null> => {
    if (!companyId) return null;

    try {
      // TIPO-CODIGO-UX: o código vem do sistema, nunca da tela; na corrida com outro tipo igual, gera de novo uma vez
      const insertType = async () =>
        supabase
          .from('product_types')
          .insert({
            company_id: companyId,
            name: data.name,
            internal_code: await generateInternalCode(data.name),
            description: data.description || null,
            allows_rental: data.allows_rental,
            allows_sale: data.allows_sale,
            requires_serial_tracking: data.requires_serial_tracking,
            allows_manufacturing: data.allows_manufacturing ?? false,
            allows_variation: data.allows_variation ?? true, // GRADE-UX parte 3, ajustes (I.178, D6)
            icon: data.icon || 'Package',
          })
          .select()
          .single();
      let { data: newType, error } = await insertType();
      if (error?.code === '23505' && `${error.message ?? ''} ${error.details ?? ''}`.includes('uq_product_types_company_code')) {
        ({ data: newType, error } = await insertType());
      }

      if (error) throw error;

      toast({
        title: 'Tipo de produto criado',
        description: `"${data.name}" foi adicionado com sucesso`,
      });

      await fetchProductTypes();
      return newType as unknown as ProductType;
    } catch (error: any) {
      console.error('Erro ao criar tipo de produto:', error);
      
      const known = productTypeErrorMessage(error);
      if (known) {
        toast({ title: known.title, description: known.description, variant: 'destructive' });
      } else {
        toast({
          title: 'Erro',
          description: 'Não foi possível criar o tipo de produto',
          variant: 'destructive',
        });
      }
      return null;
    }
  };

  const updateProductType = async (id: string, data: Partial<ProductTypeFormData>): Promise<boolean> => {
    try {
      // TIPO-CODIGO-UX (I.178): a edição nunca manda o código — ele fica fixo depois de criado (inclusive ao renomear;
      // tipo sem código continua sem, a correção é do EMPRESA-SEED-TIPOS)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { internal_code: _ignoredCode, ...fields } = data;
      const { error } = await supabase
        .from('product_types')
        .update({
          ...fields,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Tipo de produto atualizado',
        description: 'As alterações foram salvas',
      });

      await fetchProductTypes();
      return true;
    } catch (error: any) {
      console.error('Erro ao atualizar tipo de produto:', error);
      
      const known = productTypeErrorMessage(error);
      if (known) {
        toast({ title: known.title, description: known.description, variant: 'destructive' });
      } else {
        toast({
          title: 'Erro',
          description: 'Não foi possível atualizar o tipo de produto',
          variant: 'destructive',
        });
      }
      return false;
    }
  };

  const deleteProductType = async (id: string): Promise<boolean> => {
    try {
      // Soft delete - apenas marca como inativo
      const { error } = await supabase
        .from('product_types')
        .update({ active: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Tipo de produto removido',
        description: 'O tipo de produto foi desativado',
      });

      await fetchProductTypes();
      return true;
    } catch (error) {
      console.error('Erro ao remover tipo de produto:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível remover o tipo de produto',
        variant: 'destructive',
      });
      return false;
    }
  };

  const getProductTypeById = (id: string): ProductType | undefined => {
    return productTypes.find(pt => pt.id === id);
  };

  const getProductTypeByInternalCode = (code: string): ProductType | undefined => {
    return productTypes.find(pt => pt.internal_code === code);
  };

  // Helper: tipos que permitem locação
  const getRentalProductTypes = () => {
    return productTypes.filter(pt => pt.allows_rental);
  };

  // Helper: tipos que requerem rastreamento serial
  const getSerialTrackingTypes = () => {
    return productTypes.filter(pt => pt.requires_serial_tracking);
  };

  // Helper: tipos que permitem venda
  const getSaleProductTypes = () => {
    return productTypes.filter(pt => pt.allows_sale);
  };

  // Helper: tipos que permitem produção/manufatura
  const getManufacturingProductTypes = () => {
    return productTypes.filter(pt => pt.allows_manufacturing);
  };

  // Helper: verificar se um tipo permite locação pelo internal_code
  const allowsRental = (internalCode: string): boolean => {
    const pt = getProductTypeByInternalCode(internalCode);
    return pt?.allows_rental ?? false;
  };

  // Helper: verificar se um tipo requer rastreamento pelo internal_code
  const requiresSerialTracking = (internalCode: string): boolean => {
    const pt = getProductTypeByInternalCode(internalCode);
    return pt?.requires_serial_tracking ?? false;
  };

  return {
    productTypes,
    isLoading,
    fetchProductTypes,
    createProductType,
    updateProductType,
    deleteProductType,
    getProductTypeById,
    getProductTypeByInternalCode,
    // Novos helpers
    getRentalProductTypes,
    getSerialTrackingTypes,
    getSaleProductTypes,
    getManufacturingProductTypes,
    allowsRental,
    requiresSerialTracking,
  };
};
